import {
  useEffect,
  useState,
} from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import axios from 'axios'
import qs from 'qs'
import Swal from 'sweetalert2'
import {
  MaterialReactTable,
  useMaterialReactTable,
} from 'material-react-table'
import {
  MRT_Localization_ID,
} from 'material-react-table/locales/id'
import {
  MRT_Localization_EN,
} from 'material-react-table/locales/en'
import { LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import {
  ROUTE_PATHS,
  toScientific,
  twoDecimalFormat,
  dateSortingFn,
  datePeriod,
} from '../../../library/helper'
import DateCell from '../../../components/DateCell'

const useTableLookup = () => {
  const scope3CategoryId = 5 // Fixed category ID for scope3category5

  return useQuery({
    queryKey: [
      'external-waste-emission-record-summary-lookup',
      'scope3category5',
    ],
    retry: false,
    queryFn: ({ signal }) =>
      axios({
        url: [
          process.env.REACT_APP_BASE_SERVER,
          '/external-waste-emission-record-summary-lookup',
        ].join(''),
        method: 'GET',
        withCredentials: true,
        signal,
        params: { scope3CategoryId },
      }).then((response) => {
        console.log("Data Response:", response?.data?.data); // Cek data yang diterima
        return response?.data?.data ?? [];
      }),
  })
}


const useTable = (account, optionClient) => {
  const { t, i18n } = useTranslation()
  const [isLoadingAccount, setIsLoadingAccount] = useState(false)

  const [columnVisibility, setColumnVisibility] = useState({
    record_date: true, // ada
    record_total: true, // ada
    company_name: true, //ada
    office_name:true, //ada
    record_uuid: true,
    record_amount: true,
    unit_label:true,
    client_name:true
  })

  useEffect(() => {
    setIsLoadingAccount(true)

    const showRecordUuid = account?.is_parent === 0
    const showClientName = account?.is_parent === 1

    setColumnVisibility((prev) => ({
      ...prev,
      record_uuid: showRecordUuid,
      client_name: showClientName,
    }))

    setIsLoadingAccount(false)
  }, [account, optionClient])

  const { data = [], isLoading, isRefetching } = useTableLookup()

  const onDeleteCall = async (payload) => {
    const response = await axios({
      withCredentials: true,
      method: 'POST',
      headers: { 'content-type': 'application/x-www-form-urlencoded' },
      data: qs.stringify(payload),
      url: `${process.env.REACT_APP_BASE_SERVER}/emission/delete`,
    })
    return response.data.success
  }

  const onDelete = async (id) => {
    Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, delete it!',
    }).then(async (result) => {
      if (result.isConfirmed) {
        const success = await onDeleteCall({ id })
        if (success) {
          Swal.fire({
            title: 'Deleted',
            text: 'Your data has been deleted.',
            confirmButtonColor: '#3085d6',
            confirmButtonText: 'Ok',
          }).then((r) => r.isConfirmed && window.location.reload())
        } else {
          Swal.fire({
            icon: 'error',
            title: '',
            html: '<p>Cannot delete data, please try again later</p>',
            confirmButtonColor: '#3085d6',
            confirmButtonText: 'Ok',
          })
        }
      }
    })
  }

  const scope3CategoryId = 5 // Fixed category ID for scope3category5

  return useMaterialReactTable({
    enableBottomToolbar: true,
    enableColumnOrdering: true,
    enableDensityToggle: false,
    enableExpandAll: false,
    enableFullScreenToggle: false,
    enableGlobalFilter: false,
    enablePagination: true,
    enableTableFooter: false,
    enableTopToolbar: true,

    initialState: {
      density: 'compact',
      columnVisibility,
    },

    muiTableProps: { elevation: 0 },
    state: {
      isLoading: isLoadingAccount || isRefetching,
      columnDropdownVisibility: { ...columnVisibility, record_uuid: false },
      columnVisibility,
    },

    localization: i18n.language === 'id'
      ? MRT_Localization_ID
      : MRT_Localization_EN,

    columns: [
      {
        accessorKey: 'client_name',
        header: t('Site'),
        size: 100,
        Cell: ({ cell }) => cell.getValue(),
        enableColumnFilter: true,
        enableSorting: false,
        enableColumnActions: false,
        enableResizing: false,
        filterFn: 'equals',
        enableHiding: columnVisibility.client_name,
      },
        {
          header: t('emission.table.label1'),
          size: 100,
          accessorKey: 'record_date',
          accessorFn: (row) => new Date(row.record_date),
          Cell: ({ row }) => <DateCell dateTime={row.original.record_date} />,
          sortingFn: dateSortingFn,
        },
      {
        accessorKey: 'office_name',
        header: t('emission.table.label2'),
        size: 100,
      },
            {
        accessorKey: 'company_name',
        header: t('emission.table.company'),
        size: 100,
      },
      {
        accessorKey: 'record_amount',
        header: t('emission.table.label5'),
        size: 100,
          Cell: ({ cell, row }) => <div className="d-flex flex-column flex-wrap gap-2">
          <span>
                {row.original.record_amount} {row.original.unit_label}
              </span>
        </div>,
      },
      {
        accessorKey: 'record_total',
        header: t('emission.table.label7'),
        size: 100,
        Cell: ({ cell }) =>
          cell.getValue() == null ? (
            ''
          ) : (
          <span title={cell.getValue()}>{toScientific(cell.getValue())}</span>
          ),
      },
       {
          accessorKey: 'record_uuid',
          header: t('emission.table.label6'),
          size: 100,
          enableSorting: false,
          enableColumnFilter: false,
          enableGrouping: false,
          enableHiding: columnVisibility.record_uuid,
          Cell: ({ cell }) => (
            <div className="d-flex flex-wrap gap-2">
              <Link to={`/scope3/category5/update/${cell.getValue()}`} className="btn btn-info btn-icon waves-effect waves-light">
                <i className="ri-pencil-fill"></i>
              </Link>
              <button onClick={() => onDelete(cell.getValue())} type="button" className="btn btn-warning btn-icon waves-effect waves-light">
                <i className="ri-delete-bin-2-fill"></i>
              </button>
            </div>
          ),
        },
    ],
    data,
  })
}

export default function EmissionScope3Category5() {
  const account = useSelector((state) => state.account.value)
  const { t } = useTranslation()

  const [selectedClient, setSelectedClient] = useState('')
  const [optionClient, setOptionClient] = useState([])

  const table = useTable(account, optionClient)

  const onSelectClient = (event) => {
    const value = event.target.value
    setSelectedClient(value)

    if (!table) return
    if (value === '') {
      table.getColumn('client_name')?.setFilterValue(undefined)
    } else {
      const found = optionClient.find(
        (item) => item.company_client === value
      )
      if (found) {
        table.getColumn('client_name')?.setFilterValue(found.company_name)
      }
    }
  }

  const btnCreateInBulkLink = ROUTE_PATHS.scope3category5Create
  const pageTitle = t('menu.scope3Category5Title')

  useEffect(() => {
    const controller = new AbortController()

    const fetchClients = async () => {
      try {
        const resp = await axios({
          withCredentials: true,
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          url: `${process.env.REACT_APP_BASE_SERVER}/client/get_all`,
          signal: controller.signal,
        })
        if (resp?.data?.ok && Array.isArray(resp.data.data)) {
          setOptionClient(resp.data.data)
        }
      } catch (e) {
        if (e.code !== 'ERR_CANCELED') {
          const msg =
            typeof e.response?.data?.message === 'string'
              ? e.response.data.message
              : e.message
          Swal.fire({
            icon: 'error',
            title: '',
            html: `<p>${msg}</p>`,
            confirmButtonColor: '#3085d6',
            confirmButtonText: 'Ok',
          })
        }
      }
    }

    fetchClients()
    return () => controller.abort()
  }, [])

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <div className="page-content">
        <div className="container-fluid">
          <div className="row">
            <div className="col-12">
              <div className="page-title-box d-sm-flex align-items-center justify-content-between">
                <h4 className="mb-sm-0">{pageTitle}</h4>
              </div>
            </div>
          </div>

          <div className="row">
            <div className="col-lg-12">
              <div className="card">
                <div className="card-header">
                  <h5 className="card-title mb-0">{t('general.read')}</h5>
                  <br />
                  <div className="d-flex justify-content-between flex-wrap">
                    <div className="gap-2 d-flex flex-wrap">
                      {account.client_subscription > 0 &&
                      account.is_parent === 0 ? (
                        <Link
                          to={btnCreateInBulkLink}
                          className="btn btn-success"
                        >
                          {t('general.btnCreateInBulk')}
                        </Link>
                      ) : null}
                    </div>

                    {account.is_parent === 1 && (
                      <select
                        style={{
                          width: 'fit-content',
                          padding: '0.3rem 2.1rem 0.3rem 0.9rem',
                        }}
                        onChange={onSelectClient}
                        value={selectedClient}
                        id="companyClient"
                        name="companyClient"
                        className="form-select form-select-sm"
                      >
                        <option value="">--</option>
                        {optionClient?.map((item) => (
                          <option
                            key={item.company_client}
                            value={item.company_client}
                          >
                            {item.company_name}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                </div>

                <div className="card-body">
                  <MaterialReactTable table={table} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </LocalizationProvider>
  )
}
